import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { CalendarComponent } from '@at/shared';
import { DisplayNameComponent } from '@at/user';
import { MembersFilterComponent, MemberMap, GroupedMemberMap, IsTripOwnerPipe } from '@at/trip';
import { CURRENT_TRIP_MEMBERS, membersFilter } from '@at/trip/people';
import { DatesService } from '../../../services/dates.service';
import { weekdaysDateFilter } from '../../../helpers/dates-filter';
import { availabilityClass } from '../../../helpers/availability-class';
import { AvailabilityStatus, UserAvailability } from '../../../entities';
import { NgTemplateOutlet } from '@angular/common';
import { datesFromRange } from '../../../helpers';
import { CalendarRangePickerComponent } from '../../../../../../shared/components/calendar/calendar-range-picker/calendar-range-picker.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DateRange } from '@angular/material/datepicker';
import { eachDayOfInterval, endOfDay, interval, isSameDay, isWithinInterval } from 'date-fns';

type UserAvailabilityMetadata = UserAvailability & {
  percentage: number;
  availableDays: number;
  totalDays: number;
};

@Component({
  selector: 'at-availability-map',
  standalone: true,
  imports: [
    CalendarComponent,
    DisplayNameComponent,
    MembersFilterComponent,
    NgTemplateOutlet,
    CalendarRangePickerComponent,
    ReactiveFormsModule,
    IsTripOwnerPipe
  ],
  templateUrl: './availability-map.component.html',
  styleUrl: './availability-map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailabilityMapComponent {
  #datesService = inject(DatesService);
  #tripParticipants = inject(CURRENT_TRIP_MEMBERS);

  public rangeForm = input<FormGroup>(
    new FormGroup({
      start: new FormControl<Date | null>(null),
      end: new FormControl<Date | null>(null),
    }),
  );

  protected readonly state = this.#datesService.datesSummary;
  protected readonly startDate = computed(() => this.state()?.data?.rangeCriteria?.start ?? null);
  protected readonly endDate = computed(() => this.state()?.data?.rangeCriteria?.end ?? null);
  public selectedDates = model<DateRange<Date> | null>(null);
  protected readonly filterValue = model<string | undefined>(undefined);
  protected readonly isFilterResultEmpty = computed(() => {
    const responses = this.filteredResponses();
    const filterValue = this.filterValue();
    return (
      responses.NotReplied.length === 0 &&
      responses.FullyAvailable.length === 0 &&
      responses.PartiallyAvailable.length === 0 &&
      responses.Unavailable.length === 0 &&
      !!filterValue
    );
  });

  protected readonly participantsResponses = computed<MemberMap<UserAvailabilityMetadata>[]>(() => {
    const responses = this.state()?.data?.groupResponses?.availability?.membersAvailability;
    const participants = this.#tripParticipants();
    const totalDays = this.possibleDates().length;
    if (!responses || !participants) return [];

    return participants.map((participant) => {
      const response = responses.find((r) => r.userId === participant.userId);

      return response
        ? {
            ...response,
            ...participant,
            percentage: Math.floor((response.availability.length / totalDays) * 100),
            availableDays: response.availability.length,
            totalDays,
          }
        : { availability: [], ...participant, percentage: 0, availableDays: 0, totalDays };
    });
  });

  protected readonly filteredResponses = computed(() => {
    const filter = this.filterValue();
    const members = this.participantsResponses();
    const filteredMembers = filter ? membersFilter(members, filter) : members;

    /* const startDate = selectedDate?.start;
    if (startDate) {
      responses = this.#filterMembersByAvailabilityOfDateRangeCriteria(
        selectedDate,
        startDate,
        responses,
      );
    } */

    const groups = this.#getMembersMap(filteredMembers);

    groups.PartiallyAvailable = groups.PartiallyAvailable.sort(
      (a, b) => b.percentage - a.percentage,
    );

    return groups;
  });

  protected readonly possibleDates = computed(() => {
    return datesFromRange(this.state()?.data?.rangeCriteria);
  });
  protected readonly getDateClass = computed(() => {
    const members = this.#tripParticipants();
    return members ? availabilityClass(this.state()?.data, members.length) : undefined;
  });
  protected readonly dateFilter = computed(() =>
    weekdaysDateFilter(this.state()?.data?.rangeCriteria),
  );

  protected clearFilter() {
    this.filterValue.set(undefined);
    this.selectedDates.set(null);
  }

  public repliesCount = computed(() => {
    const total = this.#tripParticipants()?.length ?? 1;
    const replied =
      this.state()?.data?.groupResponses?.availability?.membersAvailability?.length ?? 0;
    const percentage = Math.floor((replied / total) * 100);
    return {
      total,
      replied,
      percentage,
    };
  });

  #filterMembersByAvailabilityOfDateRangeCriteria(
    selectedDates: DateRange<Date>,
    startDate: Date,
    responses: MemberMap<UserAvailabilityMetadata>[],
  ) {
    const endDate = selectedDates?.end ? selectedDates.end : endOfDay(startDate);
    const comparableDates = this.possibleDates().filter((date) => {
      return isWithinInterval(date, interval(startDate, endDate));
    });

    responses = responses.filter((participant) =>
      comparableDates.every(
        (pDate) => participant.availability.find((aDate) => isSameDay(aDate, pDate)) !== undefined,
      ),
    );
    return responses;
  }

  #getMembersMap(
    members: MemberMap<UserAvailabilityMetadata>[],
  ): GroupedMemberMap<AvailabilityStatus, UserAvailabilityMetadata> {
    const comparableDates = this.#getComparableDates();

    return members.reduce(
      (acc: GroupedMemberMap<AvailabilityStatus, UserAvailabilityMetadata>, participant) => {
        const hasResponse = participant.availability.length > 0;
        const numberOfAvailableDays = comparableDates.filter((date) =>
          participant.availability.find((aDate) => isSameDay(aDate, date)),
        ).length;
        const isFullyAvailable = numberOfAvailableDays === comparableDates.length;
        const isUnavailable = numberOfAvailableDays === 0;

        participant.totalDays = comparableDates.length;
        participant.availableDays = numberOfAvailableDays;
        participant.percentage = Math.floor((numberOfAvailableDays / comparableDates.length) * 100);

        if (!hasResponse) {
          acc.NotReplied.push(participant);
        } else if (isFullyAvailable) {
          acc.FullyAvailable.push(participant);
        } else if (isUnavailable) {
          acc.Unavailable.push(participant);
        } else {
          acc.PartiallyAvailable.push(participant);
        }

        return acc;
      },
      {
        FullyAvailable: [],
        PartiallyAvailable: [],
        Unavailable: [],
        NotReplied: [],
      },
    );
  }

  #getComparableDates() {
    const selectedDates = this.selectedDates();
    const startDate = selectedDates?.start;
    const endDate = selectedDates?.end;
    return startDate
      ? eachDayOfInterval(interval(startDate, endDate ?? startDate))
      : this.possibleDates();
  }
}
